/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AJANIN BELLEK DİZİNİ — `memory_20250818` istemci aracının sözleşmesi
 * (Faz 9 X2, 1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Anthropic'in bellek aracı İSTEMCİ tarafı: model `/memories` altında dosya
 * işlemleri (view / create / str_replace / insert / delete / rename) istiyor,
 * YÜRÜTMEYİ biz yapıyoruz. Depo BİZİM veritabanımız (`agent_memory_files`) —
 * Anthropic'in barındırılan belleği (memory stores) bilerek KULLANILMIYOR:
 * müşteri verisi kiracı yalıtımıyla bizde kalmalı, silinebilmeli (KVKK).
 *
 * Tek ağaç, üç kök — modelin gördüğü düzen:
 *
 *   /memories/workspace/…  ÇALIŞMA ALANI BELLEĞİ. Ekibin ve arka plan
 *                          operatörünün ortak hafızası (marka sesi, hedefler,
 *                          kampanya düzeni). Herkes okur; YALNIZ yönetici
 *                          (ADMIN+) ve yönetici yetkisiyle çalışan operatör
 *                          yazar — `remember` aracıyla AYNI karar (Faz 4/8 E):
 *                          bu bellek otonom kararları yönlendiriyor.
 *   /memories/personal/…   KİŞİSEL BELLEK. Yalnız sohbetteki kişi okur ve
 *                          yazar (her rol); operatör ve başka üye görmez.
 *   /memories/notes/…      ORTAK NOTLAR — `remember`/`forget`/`list_notes`in
 *                          yönettiği kısa notların SALT OKUMA görünümü
 *                          (`<id>.md`). Yazma bu kökte reddedilir: notlar
 *                          kendi araçlarıyla değişir (tekrar yazma koruması,
 *                          iğne, süre). Böylece iki hafıza tek ağaçta durur
 *                          ama tek bir kayıt iki yoldan değişmez.
 *
 * Kemerler (sunucuda, `agent-memory.service.ts`):
 *   · yol — yalnız bu üç kök; `..`, `.` ile başlayan parça, ters bölü, yüzde
 *     kodlaması, boş parça REDDEDİLİR (çözümleme yok, reddetme var)
 *   · boyut — dosya, kök toplamı ve dosya sayısı tavanı (aşağıda)
 *   · kişisel veri — e-posta, telefon, kart numarası, IBAN, jeton biçimleri
 *     yazılmadan önce `[redacted …]` olur ve model bunu sonuçta görür
 *   · leke — web / MCP içeriği okunan konuşmada ORTAK bellek yazılmaz
 *     (bir web sayfasının talimatı kalıcı hafızaya ve oradan operatöre
 *     sızmasın); kişisel bellek yazılabilir
 */

/** Modelin gördüğü kök. */
export const MEMORY_ROOT = '/memories';

/** Kökün altındaki üç dizin — sıra ekran ve listeleme sırası. */
export const MEMORY_SCOPES = ['workspace', 'personal', 'notes'] as const;
export type MemoryScopeDir = (typeof MEMORY_SCOPES)[number];

/** Veritabanında saklanan iki kapsam (notlar ayrı tablo — `operator_notes`). */
export type MemoryStoredScope = 'WORKSPACE' | 'USER';

export const AGENT_MEMORY_LIMITS = {
  /** Tek dosyanın en fazla karakteri — `view` dosyayı kesmeden döndürsün (sağlayıcı 16K'da kesiyor). */
  maxFileChars: 12_000,
  /** Kök başına toplam karakter. */
  maxScopeChars: { workspace: 120_000, personal: 40_000 } as const,
  /** Kök başına dosya sayısı. */
  maxFiles: { workspace: 60, personal: 30 } as const,
  /** Kökten sonraki göreli yolun en fazla karakteri. */
  maxPathChars: 160,
  /** İç içe dizin derinliği (dosya adı dahil parça sayısı). */
  maxDepth: 4,
} as const;

/** Bir yol parçası: harf, rakam, nokta, tire, alt çizgi; nokta ile BAŞLAYAMAZ. */
const SEGMENT = /^[A-Za-z0-9_][A-Za-z0-9._-]{0,79}$/;

export type MemoryPath =
  | { kind: 'root' }
  | { kind: 'scope'; scope: MemoryScopeDir }
  | { kind: 'entry'; scope: MemoryScopeDir; rel: string };

export type MemoryPathError =
  | 'OUTSIDE_ROOT'
  | 'TRAVERSAL'
  | 'BAD_SEGMENT'
  | 'TOO_LONG'
  | 'TOO_DEEP'
  | 'UNKNOWN_SCOPE';

/**
 * Modelin verdiği yolu çözer — SAF, normalleştirmez, REDDEDER.
 *
 * `path.resolve` gibi bir çözümleme bilerek YOK: `/memories/a/../b` çözülüp
 * kabul edilseydi kural "çözülen yol kökte mi" sorusuna kalırdı; burada
 * `..`'nın kendisi yasak. Sondaki tek `/` dizin görünümü için tolere ediliyor.
 */
export function parseMemoryPath(raw: unknown): { ok: true; path: MemoryPath } | { ok: false; error: MemoryPathError } {
  if (typeof raw !== 'string') return { ok: false, error: 'OUTSIDE_ROOT' };
  const value = raw.trim();
  if (value.length > MEMORY_ROOT.length + 1 + 20 + AGENT_MEMORY_LIMITS.maxPathChars) return { ok: false, error: 'TOO_LONG' };
  /* Kodlanmış geçiş (`%2e%2e%2f`), ters bölü, NUL ve kontrol karakteri —
     hiçbiri meşru bir bellek yolunda yok. */
  // eslint-disable-next-line no-control-regex
  if (/%|\\|[\u0000-\u001f\u007f]/.test(value)) return { ok: false, error: 'TRAVERSAL' };
  if (value !== MEMORY_ROOT && !value.startsWith(`${MEMORY_ROOT}/`)) return { ok: false, error: 'OUTSIDE_ROOT' };
  const trimmed = value.endsWith('/') && value.length > 1 ? value.slice(0, -1) : value;
  const parts = trimmed.split('/').slice(2); // '', 'memories', …
  if (parts.length === 0) return { ok: true, path: { kind: 'root' } };
  if (parts.some((part) => part === '..' || part === '.')) return { ok: false, error: 'TRAVERSAL' };
  if (parts.some((part) => part.length === 0)) return { ok: false, error: 'BAD_SEGMENT' };
  const [scope, ...rest] = parts;
  if (!(MEMORY_SCOPES as readonly string[]).includes(scope as string)) return { ok: false, error: 'UNKNOWN_SCOPE' };
  if (rest.length === 0) return { ok: true, path: { kind: 'scope', scope: scope as MemoryScopeDir } };
  if (rest.length > AGENT_MEMORY_LIMITS.maxDepth) return { ok: false, error: 'TOO_DEEP' };
  if (!rest.every((part) => SEGMENT.test(part))) return { ok: false, error: 'BAD_SEGMENT' };
  const rel = rest.join('/');
  if (rel.length > AGENT_MEMORY_LIMITS.maxPathChars) return { ok: false, error: 'TOO_LONG' };
  return { ok: true, path: { kind: 'entry', scope: scope as MemoryScopeDir, rel } };
}

/** Kapsam dizini + göreli yol → modelin gördüğü mutlak yol. */
export function memoryPathOf(scope: MemoryScopeDir, rel?: string): string {
  return rel ? `${MEMORY_ROOT}/${scope}/${rel}` : `${MEMORY_ROOT}/${scope}`;
}

/** Veritabanı kapsamı ↔ dizin. */
export function memoryDirOfScope(scope: MemoryStoredScope): Exclude<MemoryScopeDir, 'notes'> {
  return scope === 'WORKSPACE' ? 'workspace' : 'personal';
}
export function memoryScopeOfDir(dir: Exclude<MemoryScopeDir, 'notes'>): MemoryStoredScope {
  return dir === 'workspace' ? 'WORKSPACE' : 'USER';
}

/**
 * Bellek kartındaki bir DOSYA satırı (memory kartı, Faz 9 X2). Dosyanın
 * METNİ karta yazılmıyor — canlı uçtan (serbest metin; not satırıyla aynı
 * karar). `op` bu kartı doğuran işlem: model az önce ne yaptı.
 */
export interface MemoryCardFile {
  fileId: string;
  /** Modelin gördüğü mutlak yol (`/memories/workspace/marka.md`). */
  path: string;
  scope: 'workspace' | 'personal';
  chars: number;
  createdBy: 'agent' | 'user';
  updatedAt: string;
  op: 'created' | 'edited' | 'renamed' | 'deleted' | null;
}
